import { MIN_VIEW_WINDOW, MAX_VIEW_WINDOW, FUTURE_PADDING } from '../../utils/chartUtils'
import { isPhoneDevice } from '../../components/hooks/useIsPhone'

/**
 * xZoomStore — the user's X view, remembered per chart slot and per device
 * class: localStorage `solcheddar:xZoom:<slot>:<desktop|phone>` =
 *   { win?: number, gap?: number }
 *   win — candles on screen (the visible COUNT, not px per candle: the same
 *         number of candles survives a panel resize / rotation). Present only
 *         after a manual X zoom; absent = width-derived default fit.
 *   gap — live candle's screen slot, as columns of empty future space right
 *         of it (end - lastIdx). Present only when it differs from the
 *         default rest (AUTO_PADDING); absent = default.
 * Read on token switch, TF change (live-slot anchor) and reload; written
 * (debounced) after an X zoom / pan settles; cleared by the footer reset.
 * Local only — settingsSync carries watchlists, not view prefs.
 */
const MAX_WIN = MAX_VIEW_WINDOW * 2   // = FPCC MAX_VIEW_WINDOW_X

export function xZoomKey(slot) {
  return `solcheddar:xZoom:${slot || 'main'}:${isPhoneDevice() ? 'phone' : 'desktop'}`
}

export function loadXView(slot) {
  try {
    const v = JSON.parse(localStorage.getItem(xZoomKey(slot)) || 'null')
    if (!v || typeof v !== 'object') return null
    const out = {}
    if (Number.isFinite(+v.win) && +v.win > 0) {
      out.win = Math.max(MIN_VIEW_WINDOW, Math.min(MAX_WIN, Math.round(+v.win)))
    }
    if (Number.isFinite(+v.gap)) out.gap = Math.max(0, Math.min(FUTURE_PADDING, Math.round(+v.gap)))
    return (out.win != null || out.gap != null) ? out : null
  } catch { return null }
}

/** Stored win, or null (= default fit). */
export function loadXWin(slot) {
  const v = loadXView(slot)
  return v && v.win != null ? v.win : null
}

/** Stored live gap clamped to a window (live candle stays on screen), or fallback. */
export function loadXGap(slot, win, fallback) {
  const v = loadXView(slot)
  if (!v || v.gap == null) return fallback
  const w = Math.max(1, Math.round(+win || 1))
  return Math.max(0, Math.min(v.gap, w - 1, FUTURE_PADDING))
}

/** Write { win?, gap? }; an empty view removes the key. Returns true if changed. */
export function saveXView(slot, view) {
  try {
    const key = xZoomKey(slot)
    const v = {}
    if (view && view.win != null) v.win = view.win
    if (view && view.gap != null) v.gap = view.gap
    const next = (v.win != null || v.gap != null) ? JSON.stringify(v) : null
    const prev = localStorage.getItem(key)
    if (prev === next) return false
    if (next == null) localStorage.removeItem(key)
    else localStorage.setItem(key, next)
    return true
  } catch { return false }
}

export function clearXView(slot) {
  try { localStorage.removeItem(xZoomKey(slot)) } catch {}
}
